import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { LiveMenuBadge } from "@/components/LiveMenuBadge";
import { DispensaryCover } from "@/components/DispensaryCover";
import { isTrustedImageUrl } from "@/lib/imageTrust";
import { FavoriteStoreButton } from "@/components/FavoriteStoreButton";
import { ShopRating } from "@/components/ShopRating";
import { useShopRatings } from "@/hooks/useShopRatings";
import { useNativeShopRatings } from "@/hooks/useNativeShopRatings";
import { useApp } from "@/contexts/AppContext";
import { MapPin } from "lucide-react";

// Great-circle distance in miles between two lat/lon points. Same formula
// used backend-side for search ranking — keeping it client-side here so
// the dispensary list can render an immediate distance sort without an
// extra API round-trip.
const haversineMi = (lat1, lon1, lat2, lon2) => {
  if ([lat1, lon1, lat2, lon2].some((v) => v == null || Number.isNaN(v))) {
    return Number.POSITIVE_INFINITY;
  }
  const R = 3958.8; // earth radius, miles
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

export default function DispensaryList() {
  const { location } = useApp();
  const [list, setList] = useState([]);

  useEffect(() => {
    api.get("/dispensaries").then((r) => setList(r.data));
  }, []);

  // Sort by distance from the user's location when we have one. Without a
  // location, fall back to alphabetical so the list isn't seed-order random.
  const sorted = useMemo(() => {
    if (!list?.length) return [];
    const arr = list.map((d) => {
      const lat = d.lat ?? d.location?.coordinates?.[1];
      const lon = d.lon ?? d.location?.coordinates?.[0];
      const distance_mi = location
        ? haversineMi(location.lat, location.lon, lat, lon)
        : null;
      return { ...d, distance_mi };
    });
    if (location) {
      arr.sort((a, b) => (a.distance_mi ?? 1e9) - (b.distance_mi ?? 1e9));
    } else {
      arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }
    return arr;
  }, [list, location]);

  const ratings = useShopRatings(sorted.map((d) => d.slug));
  const nativeRatings = useNativeShopRatings(sorted.map((d) => d.slug));

  return (
    <main className="container-narrow py-10">
      <div className="eyebrow mb-2">
        {({ PA: "Pennsylvania", CO: "Colorado", OR: "Oregon" }[(location?.state || "").toUpperCase()]) || "Pennsylvania"}
      </div>
      <h1 className="font-serif text-4xl mb-2">Dispensaries</h1>
      <p className="text-muted-foreground text-sm mb-8">
        {sorted.length} licensed shops{" "}
        {location ? (
          <>
            · sorted by distance from{" "}
            <span className="text-foreground">{location.label || `${location.city || "you"}`}</span>
          </>
        ) : (
          <>· set a location to sort by distance</>
        )}
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {sorted.map((d) => (
          <Link
            key={d.slug}
            to={`/dispensaries/${d.slug}`}
            data-testid={`disp-list-${d.slug}`}
            className="card-hover bg-card border border-border rounded-2xl overflow-hidden"
          >
            <div className="relative aspect-[16/10] overflow-hidden bg-secondary">
              {isTrustedImageUrl(d.cover_url) ? (
                <img
                  src={d.cover_url}
                  alt={d.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <DispensaryCover
                  name={d.name}
                  slug={d.slug}
                  city={d.city}
                  posHint={d.pos_hint || d.pos_detected}
                  testId={`disp-list-cover-${d.slug}`}
                />
              )}
              <div className="absolute top-2 right-2">
                <FavoriteStoreButton slug={d.slug} name={d.name} size="sm" />
              </div>
              {Number.isFinite(d.distance_mi) && (
                <div
                  data-testid={`disp-list-distance-${d.slug}`}
                  className="absolute bottom-2 left-2 inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded-full bg-background/85 backdrop-blur border border-border text-foreground"
                >
                  <MapPin className="w-3 h-3" />
                  {d.distance_mi < 10
                    ? d.distance_mi.toFixed(1)
                    : Math.round(d.distance_mi)}{" "}
                  mi
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-xs text-muted-foreground">
                    {d.city}, {d.state}
                  </div>
                  <div className="font-serif text-xl">{d.name}</div>
                </div>
                {d.live_menu && <LiveMenuBadge size="xs" />}
                {d.coming_soon && (
                  <span
                    data-testid={`disp-coming-soon-${d.slug}`}
                    className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-200 border border-emerald-500/30"
                  >
                    Coming soon
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <ShopRating
                  slug={d.slug}
                  variant="native"
                  rating={nativeRatings[d.slug]?.rating}
                  count={nativeRatings[d.slug]?.rating_count}
                />
                <ShopRating
                  slug={d.slug}
                  variant="google"
                  rating={ratings[d.slug]?.rating}
                  count={ratings[d.slug]?.rating_count}
                />
                <p className="text-xs text-muted-foreground">{d.hours}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </main>
  );
}
